import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * Recent search terms, persisted per browser.
 */
class RecentSearches {
  static #KEY = 'filamentor:recentSearches';
  static #MAX = 6;

  /** @returns {string[]} */
  static get() {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.#KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((t) => typeof t === 'string') : [];
    } catch {
      return [];
    }
  }

  /** @param {string} term */
  static add(term) {
    const clean = term.trim().replace(/\s+/g, ' ');
    if (clean.length < 2) return;
    const lower = clean.toLowerCase();
    const next = [clean, ...this.get().filter((t) => t.toLowerCase() !== lower)].slice(0, this.#MAX);
    this.#write(next);
  }

  /** @param {string} term */
  static remove(term) {
    this.#write(this.get().filter((t) => t !== term));
  }

  static clear() {
    this.#write([]);
  }

  /** @param {string[]} list */
  static #write(list) {
    try {
      if (list.length) localStorage.setItem(this.#KEY, JSON.stringify(list));
      else localStorage.removeItem(this.#KEY);
    } catch {
      /* private mode etc. */
    }
  }
}

/**
 * "Back returns to the search": remembered right before the modal navigates
 * away, consumed on the next back/forward load of a non-results page.
 */
class SearchResume {
  static #KEY = 'filamentor:searchResume';
  static #TTL = 30 * 60 * 1000;

  /**
   * Remember the term, plus where the search started (the page and its
   * history depth) so "cancel" on the results page can leave the whole
   * search, results page included. Marks made on the results page keep the
   * original origin.
   * @param {string} term
   */
  static mark(term) {
    try {
      const prev = this.record();
      const onResults = location.pathname === Theme.routes.search_url;
      const from = onResults && prev ? prev.from : location.href;
      const len = onResults && prev ? prev.len : history.length;
      sessionStorage.setItem(this.#KEY, JSON.stringify({ term: term.trim(), t: Date.now(), from, len }));
    } catch {
      /* ignore */
    }
  }

  /** @returns {{term: string, from?: string, len?: number} | null} */
  static record() {
    try {
      const raw = sessionStorage.getItem(this.#KEY);
      if (!raw) return null;
      const rec = JSON.parse(raw);
      if (typeof rec.term !== 'string' || Date.now() - rec.t > this.#TTL) {
        this.clear();
        return null;
      }
      return rec;
    } catch {
      return null;
    }
  }

  /** @returns {string | null} the term, or null when nothing (fresh) is stored */
  static peek() {
    return this.record()?.term ?? null;
  }

  static clear() {
    try {
      sessionStorage.removeItem(this.#KEY);
    } catch {
      /* ignore */
    }
  }
}

/* ------------------------------------------------------------------ */
/* Query building                                                      */
/* ------------------------------------------------------------------ */

/**
 * Shopify's plain multi-word search matches ANY word, fuzzily: "petg černá"
 * returns nearly the whole catalogue with the in-stock partial matches ahead
 * of the out-of-stock exact ones. A field-scoped term (`title:petg`) is
 * matched exactly (case- and diacritic-insensitive) and ANDed with the rest,
 * but gets no prefix matching, so the last word stays plain: it keeps
 * `options[prefix]=last` while still being ANDed with the scoped words.
 * Single words are left alone (fuzzy + prefix, as before).
 *
 * @param {string} term - what the user typed
 * @returns {{ q: string, scoped: boolean }}
 */
export function buildSearchQuery(term, data = null) {
  const raw = term
    .trim()
    .split(/\s+/)
    .map((t) => t.replace(/["'()*:]/g, '').replace(/^-+/, ''))
    .filter(Boolean);

  // `title:` only matches the source-language (Czech) title, so on the other
  // storefronts the known words are swapped for their Czech form first; a
  // translated word is complete and gets scoped too, an unknown last word
  // stays plain so it keeps prefix matching while the customer types.
  /** @type {{ text: string, exact: boolean }[]} */
  let words = [];
  const translate = data !== null && !data.locale.startsWith('cs');
  for (const token of raw) {
    const source = translate ? sourceWord(token, data) : undefined;
    if (source === null) continue;
    words.push(source === undefined ? { text: token, exact: false } : { text: source, exact: true });
  }
  if (!words.length) words = raw.map((text) => ({ text, exact: false }));

  if (words.length === 1) {
    const [word] = words;
    if (!word) return { q: '', scoped: false };
    return word.exact ? { q: `title:${word.text}`, scoped: true } : { q: word.text, scoped: false };
  }
  const last = /** @type {{ text: string, exact: boolean }} */ (words.pop());
  const head = words.map((w) => `title:${w.text}`);
  head.push(last.exact ? `title:${last.text}` : last.text);
  return { q: head.join(' '), scoped: true };
}

/**
 * @param {string} q - a query from buildSearchQuery
 * @returns {URL}
 */
export function searchPageUrl(q, { fallback = false, term = '' } = {}) {
  const url = new URL(Theme.routes.search_url, location.origin);
  url.searchParams.set('q', q);
  url.searchParams.set('type', 'product');
  url.searchParams.set('options[prefix]', 'last');
  // The strict query found nothing; the page says the hits are only similar.
  if (fallback) url.searchParams.set('fallback', '1');
  // The query carries Czech words the customer never typed: the page shows
  // `t` instead (search-page-input.js).
  if (term && q.replace(/\btitle:/g, '') !== term) url.searchParams.set('t', term);
  return url;
}

/* ------------------------------------------------------------------ */
/* Collection intent                                                   */
/* ------------------------------------------------------------------ */

/**
 * Most searches are "material + colour (+ weight, brand, finish)". Those are
 * better served by the collection page with the matching filters than by a
 * results page: same cards, add to cart, notify-me, sorting. interpretIntent
 * maps the typed words onto a collection URL; anything it cannot place makes
 * the whole query fall back to the strict product search.
 *
 * The dictionary (collections, colour families, vendors) is emitted by
 * snippets/search-intent-data.liquid. The vocabulary below is brand-neutral
 * and matched on diacritic-stripped lowercase tokens with one typo allowed
 * on words of four letters or more.
 */

/** @typedef {{ handle: string, label: string, title: string, url: string }} IntentCollection */
/** @typedef {{ id: string, label: string, handle: string }} IntentColor */
/** @typedef {{ locale: string, base: string, filters: Record<string, string>, collections: IntentCollection[], colors: IntentColor[], vendors: string[], weights: string[], strings: Record<string, string> }} IntentData */
/** @typedef {{ url: string, title: string, count?: number, images?: string[] }} Intent */

/** @type {Record<string, string[]>} handle → aliases */
const INTENT_MATERIALS = {
  pla: ['pla'],
  petg: ['petg'],
  pctg: ['pctg'],
  asa: ['asa'],
  abs: ['abs'],
  tpu: ['tpu', 'flex', 'flexibilni', 'flexible', 'flexibel', 'flexibles', 'flessibile', 'elastyczny', 'rugalmas'],
  'nylon-pa12': ['nylon', 'pa12', 'nailon'],
};

/** @type {Record<string, string[]>} modifier → aliases (Czech, English and the storefront languages) */
const INTENT_MODIFIERS = {
  matte: [
    'matna', 'matne', 'matny', 'mat', 'matt', 'matte', 'matova', 'matovy', 'mate', 'opaco', 'opaca', 'opachi', 'opache',
    'matowy', 'matowa', 'matowe', 'matta', 'matto', 'matteret', 'mata',
  ],
  silk: [
    'silk', 'silky', 'hedvabna', 'hedvabny', 'hedvabne', 'seide', 'seiden', 'seidig', 'soie', 'soyeux', 'seta', 'jedwab',
    'jedwabny', 'jedwabna', 'jedwabne', 'hodvabna', 'hodvabny', 'hodvabne', 'silke', 'selyem', 'matase', 'sedoso', 'sedosa', 'sedosos', 'zijde', 'zijden',
  ],
  cf: [
    'cf', 'karbon', 'carbon', 'uhlik', 'uhlikova', 'uhlikovy', 'uhlikove', 'carbone', 'carbonio', 'karbonowy', 'weglowy',
    'weglowe', 'wegiel', 'kulstof', 'carbono', 'szen', 'szenszal', 'koolstof', 'kohlefaser', 'carbonfaser', 'kohle',
  ],
  meta: ['meta'],
  glow: [
    'svitici', 'glow', 'glowing', 'luminous', 'fosforeskujici', 'leuchtend', 'nachleuchtend', 'leuchtet', 'phosphorescent',
    'fosforescente', 'fosforescent', 'fosforeszkalo', 'swiecacy', 'swiecaca', 'swiecace', 'selvlysende', 'lysende',
    'luminescent', 'lumineux', 'luminoso', 'luminosa', 'vilagito', 'fluorescent', 'fluorescente', 'lichtgevend', 'gloeit',
    'svetleci', 'svitivy', 'svietiaci', 'svietiaca', 'svietiace',
  ],
  plus: ['plus'],
  hs: [
    'hs', 'high', 'speed', 'rychla', 'rychly', 'rychle', 'highspeed', 'schnell', 'schnelle', 'rapide', 'rapido', 'rapida',
    'veloce', 'szybki', 'szybka', 'szybkie', 'hurtig', 'rapid', 'gyors', 'snel', 'snelle', 'fast',
  ],
};

/** material+modifier → collection handle */
const INTENT_VARIANTS = {
  'pla+matte': 'pla-matne',
  'pla+silk': 'pla-silk',
  'pla+cf': 'pla-cf',
  'pla+meta': 'pla-meta',
  'pla+glow': 'pla-svitici',
  'pla+plus': 'pla-plus',
  'pla+hs': 'hs-pla',
  'petg+matte': 'petg-matne',
  'petg+cf': 'petg-cf-filamenty-pro-3d-tisk',
  'abs+plus': 'abs',
};

/** modifier alone → collection handle (only where it is unambiguous) */
const INTENT_MODIFIER_ONLY = { silk: 'pla-silk', meta: 'pla-meta', glow: 'pla-svitici', hs: 'hs-pla' };

/**
 * Colour family handle → extra aliases. The localized family name comes from
 * the metaobject; these cover English, inflected forms (noire, nera, czarna)
 * and spellings the one-edit tolerance does not reach.
 */
const INTENT_COLOR_ALIASES = {
  cerna: ['cierna', 'cierny', 'cierne', 'black', 'noir', 'schwarze', 'schwarzes', 'schwarzer', 'noire', 'negra', 'nera', 'nere', 'czarna', 'czarne', 'preta', 'neagra', 'negre', 'sorte', 'zwarte'],
  bila: ['biela', 'biely', 'biele', 'white', 'blanc', 'wit', 'weiss', 'weisse', 'weisser', 'weisses', 'blanche', 'blanca', 'bianca', 'biala', 'biale', 'branca', 'alba', 'hvide', 'witte'],
  cervena: ['red', 'rot', 'rote', 'roter', 'rotes', 'rouge', 'rojo', 'roja', 'rosso', 'rossa', 'czerwona', 'czerwone', 'vermelho', 'vermelha', 'rosu', 'rosie', 'rod', 'rode', 'rood', 'piros', 'voros'],
  fialova: ['purple', 'violet', 'violett', 'lilac', 'lila', 'viola', 'morado', 'morada', 'purpura', 'fioletowy', 'fioletowa', 'fioletowe', 'roxo', 'roxa', 'violeta', 'mov', 'paars', 'paarse'],
  hneda: ['brown', 'braun', 'braune', 'marron', 'marrone', 'brazowy', 'brazowa', 'brazowe', 'castanho', 'castanha', 'maro', 'brun', 'brune', 'bruin', 'bruine', 'barna'],
  medena: ['copper', 'kupfer', 'cuivre', 'cobre', 'rame', 'miedziany', 'miedz', 'aramiu', 'kobber', 'koper', 'rez', 'vorosrez'],
  modra: ['blue', 'navy', 'blau', 'blaue', 'bleu', 'bleue', 'azul', 'blu', 'niebieski', 'niebieska', 'niebieskie', 'albastru', 'albastra', 'bla', 'blaa', 'blauw', 'blauwe', 'kek'],
  oranzova: ['orange', 'naranja', 'arancione', 'arancio', 'pomaranczowy', 'pomaranczowa', 'pomaranczowe', 'laranja', 'portocaliu', 'portocalie', 'oranje', 'narancs', 'narancssarga'],
  pruhledna: ['priehladna', 'priehladny', 'priehladne', 'transparent', 'clear', 'natural', 'naturalni', 'prirodni', 'transparente', 'trasparente', 'przezroczysty', 'przezroczysta', 'przezroczyste', 'transparentny', 'klar', 'durchsichtig', 'naturel', 'naturale', 'atlatszo', 'gennemsigtig', 'transparant', 'doorzichtig'],
  ruzova: ['pink', 'rose', 'rosa', 'rozowy', 'rozowa', 'rozowe', 'roz', 'lyserod', 'roze', 'rozsaszin'],
  seda: ['siva', 'sivy', 'sive', 'grey', 'gray', 'grau', 'graue', 'gris', 'grigio', 'grigia', 'szary', 'szara', 'szare', 'cinza', 'cinzento', 'gri', 'graa', 'gra', 'grijs', 'grijze', 'szurke'],
  stribrna: ['strieborna', 'strieborny', 'strieborne', 'silver', 'silber', 'argent', 'argente', 'plata', 'plateado', 'argento', 'srebrny', 'srebrna', 'prateado', 'argintiu', 'solv', 'zilver', 'ezust'],
  vicebarevna: ['viacfarebna', 'viacfarebny', 'viacfarebne', 'multicolor', 'multicolour', 'multi', 'dual', 'tri', 'rainbow', 'duhova', 'duhovy', 'duha', 'mehrfarbig', 'regenbogen', 'multicolore', 'arcobaleno', 'wielokolorowy', 'wielokolorowe', 'tecza', 'teczowy', 'regnbue', 'flerfarvet', 'meerkleurig', 'regenboog', 'tobbszinu', 'szivarvany', 'curcubeu', 'arcoiris', 'multicolorido'],
  zelena: ['green', 'grun', 'grune', 'gruner', 'vert', 'verte', 'verde', 'zielony', 'zielona', 'zielone', 'gron', 'gronne', 'groen', 'groene', 'zold'],
  zlata: ['gold', 'golden', 'or', 'dore', 'doree', 'oro', 'dorado', 'dorada', 'zloty', 'zlota', 'zlote', 'dourado', 'auriu', 'guld', 'goud', 'gouden', 'arany'],
  zluta: ['zlta', 'zlty', 'zlte', 'yellow', 'gelb', 'gelbe', 'jaune', 'amarillo', 'amarilla', 'giallo', 'gialla', 'zolty', 'zolta', 'zolte', 'amarelo', 'amarela', 'galben', 'galbena', 'gul', 'gule', 'geel', 'gele', 'sarga'],
};

/** vendor (lowercase) → extra aliases */
const INTENT_VENDOR_ALIASES = { 'prof. lab': ['proflab', 'professional'], 'smart print': ['smartprint'] };

const INTENT_AVAILABLE = [
  'skladem', 'skladom', 'dostupne', 'dostupny', 'instock', 'available', 'stock', 'lager', 'vorratig', 'voorraad', 'disponible',
  'disponibles', 'disponibile', 'disponibili', 'dostepny', 'dostepna', 'dostepne', 'disponivel', 'raktaron', 'raktar', 'stoc',
  'stanie', 'magazzino',
];

/** Words that carry no meaning for the intent and are skipped. */
const INTENT_FILLER = new Set([
  'filament', 'filamenty', 'filamentu', 'filaments', 'filamente', 'filamento', 'filamentos', 'filamenti', 'filamenter',
  'filamentek', 'filamentet', 'filamentul', 'struna', 'struny', 'tiskova', 'tiskovy', 'tlacova', 'tlacovy',
  '3d', 'tisk', 'tisku', 'tiskarna', 'tiskarnu', 'tlac', 'tlace', 'tlaciaren', 'tlaciarne', 'pro', 'pre', 'na', 'do', 'a', 'i', 'v', 'ks', 'kg', 'the', 'for', 'in',
  'tmave', 'tmava', 'tmavy', 'svetle', 'svetla', 'svetly', 'pastelova', 'pastelovy', 'pastelove', 'pastel',
  'barva', 'barvy', 'barevna', 'farba', 'farby', 'farebna', 'color', 'colour', 'dark', 'light',
  'fur', 'den', 'die', 'der', 'das', 'druck', 'drucker', '3ddruck', 'impression', 'imprimante', 'impresion', 'impresora',
  'stampa', 'stampante', 'druk', 'druku', 'drukarki', 'drukarka', 'impressao', 'impressora', 'print', 'printer', 'printing',
  'printen', 'nyomtatas', 'nyomtatashoz', 'nyomtato', 'imprimare', 'imprimanta', 'pentru', 'para', 'pour', 'per', 'voor',
  'til', 'de', 'di', 'du', 'la', 'le', 'les', 'el', 'los', 'las', 'il', 'lo', 'gli', 'het', 'een', 'og', 'en', 'und', 'oder',
  'et', 'y', 'e', 'o', 'w', 'z', 'auf', 'op', 'em', 'im', 'am', 'fiber', 'fibre', 'faser', 'fibra', 'wlokno', 'wlokna',
  'kolor', 'farbe', 'couleur', 'colore', 'cor', 'farve', 'szin', 'culoare', 'kleur', 'hell', 'dunkel', 'clair', 'fonce',
  'chiaro', 'scuro', 'claro', 'oscuro', 'jasny', 'ciemny', 'lys', 'mork', 'vilagos', 'sotet', 'deschis', 'inchis', 'licht',
  'donker', 'escuro', 'helder', 'with', 'mit', 'avec', 'con', 'com', 'med', 'cu', 'met',
]);

/** @param {string} s */
const foldText = (s) =>
  s
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/ø/g, 'o')
    .replace(/ł/g, 'l')
    .replace(/æ/g, 'ae')
    .replace(/œ/g, 'oe')
    .replace(/đ/g, 'd')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Edit distance ≤ 1 (one insertion, deletion or substitution).
 * @param {string} a
 * @param {string} b
 */
function withinOneEdit(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else {
      i++;
      j++;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

/**
 * Typo tolerance: one edit, but only between words long enough that a single
 * edit cannot turn one real word into another ("gold" → "zöld", "wood" →
 * "rood", "lila" → "bílá"). Material names get 4 letters ("perg" → "petg").
 *
 * @param {string} token
 * @param {string} alias
 * @param {number} [min]
 */
const nearWord = (token, alias, min = 5) =>
  token === alias || (alias.length >= min && token.length >= min && withinOneEdit(token, alias));

/**
 * @template T
 * @param {string} token
 * @param {{ entry: T, aliases: string[] }[]} entries
 * @param {number} [min]
 * @returns {T | undefined}
 */
function matchAlias(token, entries, min = 5) {
  const exact = entries.find(({ aliases }) => aliases.includes(token));
  if (exact) return exact.entry;
  return entries.find(({ aliases }) => aliases.some((alias) => nearWord(token, alias, min)))?.entry;
}

const MATERIAL_ENTRIES = Object.entries(INTENT_MATERIALS).map(([entry, aliases]) => ({ entry, aliases }));
const MODIFIER_ENTRIES = Object.entries(INTENT_MODIFIERS).map(([entry, aliases]) => ({ entry, aliases }));
const AVAILABLE_ENTRIES = [{ entry: true, aliases: INTENT_AVAILABLE }];

/** Shore hardness ("95a", "68d") after TPU: a spec, not a filter. */
const isShore = (/** @type {string} */ token) => /^\d{2}[ad]$/.test(token);

/**
 * A word's meaning for the intent, exact matches across every category
 * before any typo tolerance, so "rosa" is pink before it is a typo of red.
 *
 * @param {string} token
 * @param {{ entry: IntentColor, aliases: string[] }[]} colorEntries
 * @param {IntentData} data
 * @returns {{ kind: 'color', color: IntentColor } | { kind: 'weight' } | { kind: 'material', material: string } | { kind: 'modifier', modifier: string } | { kind: 'available' } | { kind: 'filler' } | null}
 */
function classifyToken(token, colorEntries, data) {
  const exactColor = colorEntries.find(({ aliases }) => aliases.includes(token));
  if (exactColor) return { kind: 'color', color: exactColor.entry };
  if (data.weights.includes(token)) return { kind: 'weight' };
  const exactMaterial = MATERIAL_ENTRIES.find(({ aliases }) => aliases.includes(token));
  if (exactMaterial) return { kind: 'material', material: exactMaterial.entry };
  const exactModifier = MODIFIER_ENTRIES.find(({ aliases }) => aliases.includes(token));
  if (exactModifier) return { kind: 'modifier', modifier: exactModifier.entry };
  if (INTENT_AVAILABLE.includes(token)) return { kind: 'available' };
  if (INTENT_FILLER.has(token) || isShore(token)) return { kind: 'filler' };

  const color = matchAlias(token, colorEntries);
  if (color) return { kind: 'color', color };
  const material = matchAlias(token, MATERIAL_ENTRIES, 4);
  if (material) return { kind: 'material', material };
  const modifier = matchAlias(token, MODIFIER_ENTRIES);
  if (modifier) return { kind: 'modifier', modifier };
  if (matchAlias(token, AVAILABLE_ENTRIES)) return { kind: 'available' };
  return null;
}

/** @param {string} term */
function intentTokens(term) {
  return foldText(term)
    .replace(/\+/g, ' plus ')
    .replace(/(\d+)\s*kg\b/g, '$1kg')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/** @param {IntentData} data */
function colorAliases(data) {
  return data.colors.map((color) => ({
    entry: color,
    aliases: [foldText(color.label), color.handle, ...(INTENT_COLOR_ALIASES[color.handle] ?? [])],
  }));
}

/** modifier → the word used in the Czech product titles */
const MODIFIER_SOURCE = { matte: 'matna', silk: 'silk', cf: 'cf', meta: 'meta', glow: 'svitici', plus: 'plus', hs: 'hs' };

/**
 * The Czech form of a typed word, for the title-scoped product search on the
 * other storefronts. Colour families map to their handle (the search is
 * diacritic-insensitive, so "cerna" finds "černá"). `null` = drop the word
 * (filler, availability), `undefined` = unknown, keep as typed.
 *
 * @param {string} token
 * @param {IntentData} data
 * @returns {string | null | undefined}
 */
function sourceWord(token, data) {
  const words = intentTokens(token);
  const [word] = words;
  if (!word || words.length > 1) return undefined;
  const kind = classifyToken(word, colorAliases(data), data);
  if (!kind) return undefined;
  if (kind.kind === 'color') return kind.color.handle;
  if (kind.kind === 'modifier') return MODIFIER_SOURCE[kind.modifier];
  if (kind.kind === 'filler' || kind.kind === 'available') return null;
  return undefined;
}

/**
 * @param {string} term
 * @param {IntentData} data
 * @returns {Intent | null}
 */
export function interpretIntent(term, data) {
  const tokens = intentTokens(term);
  if (!tokens.length) return null;
  const used = tokens.map(() => false);

  /** @type {string[]} */
  const vendors = [];
  /** @type {IntentColor[]} */
  const colors = [];
  /** @type {string[]} */
  const weights = [];
  /** @type {Set<string>} */
  const modifiers = new Set();
  /** @type {string | null} */
  let material = null;
  let available = false;

  // Vendors first: they are the only multi-word phrases ("Prof. Lab").
  const vendorPhrases = data.vendors
    .flatMap((vendor) => {
      const own = intentTokens(vendor);
      const phrases = [own];
      if (own.length > 1) {
        phrases.push([own.join('')]);
        own.forEach((word) => word.length >= 4 && phrases.push([word]));
      }
      (INTENT_VENDOR_ALIASES[vendor.toLowerCase()] ?? []).forEach((alias) => phrases.push([alias]));
      return phrases.map((phrase) => ({ vendor, phrase }));
    })
    .sort((a, b) => b.phrase.length - a.phrase.length);

  for (const { vendor, phrase } of vendorPhrases) {
    for (let i = 0; i + phrase.length <= tokens.length; i++) {
      const hit = phrase.every((word, k) => !used[i + k] && nearWord(tokens[i + k] ?? '', word, 4));
      if (!hit) continue;
      phrase.forEach((_, k) => (used[i + k] = true));
      if (!vendors.includes(vendor)) vendors.push(vendor);
    }
  }

  const colorEntries = colorAliases(data);

  for (let i = 0; i < tokens.length; i++) {
    if (used[i]) continue;
    const token = tokens[i] ?? '';
    const kind = classifyToken(token, colorEntries, data);
    if (!kind) return null; // a word we cannot place: let the product search handle it
    switch (kind.kind) {
      case 'color':
        if (!colors.includes(kind.color)) colors.push(kind.color);
        break;
      case 'weight':
        if (!weights.includes(token)) weights.push(token);
        break;
      case 'material':
        if (material && material !== kind.material) return null; // two materials: not a collection
        material = kind.material;
        break;
      case 'modifier':
        modifiers.add(kind.modifier);
        break;
      case 'available':
        available = true;
        break;
      default:
        break;
    }
  }

  let handle = material;
  const mods = [...modifiers];
  if (mods.length > 1) return null;
  if (material && mods.length) {
    handle = INTENT_VARIANTS[`${material}+${mods[0]}`] ?? null;
    if (!handle) return null;
  } else if (!material && mods.length) {
    handle = INTENT_MODIFIER_ONLY[mods[0] ?? ''] ?? null;
    if (!handle) return null;
  }

  const hasFilter = colors.length > 0 || weights.length > 0 || vendors.length > 0 || available;
  if (!handle && !hasFilter) return null;

  const collection = handle ? data.collections.find((c) => c.handle === handle) : null;
  if (handle && !collection) return null;

  const url = new URL(collection ? collection.url : data.base, location.origin);
  colors.forEach((color) => url.searchParams.append(data.filters.color ?? '', color.id));
  weights.forEach((weight) => url.searchParams.append(data.filters.weight ?? '', weight));
  vendors.forEach((vendor) => url.searchParams.append(data.filters.vendor ?? '', vendor));
  if (available) url.searchParams.set(data.filters.availability ?? '', '1');

  const collectionName = collection ? collection.label : null;
  const weightLabels = weights.map((w) => w.replace(/(\d)(kg)$/, '$1 $2'));
  const title = [collectionName, ...colors.map((c) => c.label), ...weightLabels, ...vendors].filter(Boolean).join(' · ');

  return { url: url.toString(), title };
}

/**
 * @param {number} n
 * @param {Record<string, string>} strings
 */
function intentCountLabel(n, strings) {
  const form = n === 1 ? 'one' : n >= 2 && n <= 4 ? 'few' : 'many';
  return (strings[form] ?? strings.many ?? '').replace(/\d+/, String(n));
}

/**
 * The search modal: input, live results, empty state with recent searches.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @property {HTMLElement} intentSlot - collection intent row container
 * @property {HTMLElement} [recentlyViewedWrapper]
 * @property {HTMLElement[]} [recentlyViewedTitle]
 * @property {HTMLElement[]} [recentlyViewedItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** @type {number} timestamp of the last programmatic open, for the ghost-tap guard */
  #prefilledAt = 0;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // A tap that opened the modal must not also "click" whatever now sits
    // under the finger (a recent-search chip, a product row). Capture phase,
    // so it runs before any link navigates.
    this.addEventListener(
      'click',
      (event) => {
        if (Date.now() - this.#prefilledAt < 450 && event.target !== this.refs.searchInput) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      { capture: true, signal }
    );

    // Result clicks: remember the typed term, and handle the recent-search chips.
    this.addEventListener('click', this.#handleResultClick, { signal });

    this.#paintRecentSearches();

    // Warm the empty state (bestsellers, recently viewed) so the first open is instant.
    requestIdleCallback(() => this.#loadEmptyState());

    customElements.whenDefined('dialog-component').then(() => {
      if (signal.aborted) return;
      // A bare /search (no query) is never a destination: open the modal instead.
      if (location.pathname === Theme.routes.search_url && !new URLSearchParams(location.search).get('q')?.trim()) {
        this.prefill('');
        return;
      }
      this.#maybeResume(false);
      window.addEventListener('pageshow', (event) => event.persisted && this.#maybeResume(true), { signal });
    });
  }

  /**
   * Reopen the search with the last term when the visitor navigated back to
   * the page they searched from. On any other fresh navigation (except the
   * results page itself) the remembered term is dropped.
   * @param {boolean} persisted - true when restored from the back/forward cache
   */
  #maybeResume(persisted) {
    const term = SearchResume.peek();
    if (term === null) return;

    const isResultsPage = location.pathname === Theme.routes.search_url;
    const nav = /** @type {PerformanceNavigationTiming | undefined} */ (performance.getEntriesByType('navigation')[0]);
    const cameBack = persisted || nav?.type === 'back_forward';

    if (!cameBack) {
      // The page the search led to (referrer = where the search started)
      // keeps the record so "back" can reopen the modal there. Any further
      // fresh navigation drops it.
      const rec = SearchResume.record();
      const from = rec?.from?.split('#')[0] ?? '';
      const referrer = document.referrer.split('#')[0];
      const isDestination = from !== '' && referrer === from;
      if (!isResultsPage && !isDestination) SearchResume.clear();
      return;
    }

    if (isResultsPage) return; // back from a product to the results: leave the page alone

    SearchResume.clear();
    const nativeDialog = this.closest('dialog');
    if (nativeDialog?.open && !nativeDialog.matches(':modal')) {
      // bfcache dropped the top-layer state but kept the open attribute: reopen cleanly.
      nativeDialog.close();
    }
    const alreadyOpen = nativeDialog?.open && this.refs.searchInput.value.trim() === term;
    if (!alreadyOpen) this.prefill(term);
  }

  /**
   * Open the modal with a term already typed and its results loaded.
   * Used by the results-page input and by the back-navigation resume.
   * @param {string} term
   */
  prefill(term) {
    this.#prefilledAt = Date.now();
    const dialog = this.dialog;
    const nativeDialog = this.closest('dialog');
    if (dialog && nativeDialog && !nativeDialog.open) dialog.showDialog();

    const input = this.refs.searchInput;
    const clean = term.trim();
    input.value = clean;
    this.#currentIndex = -1;

    if (clean) {
      this.#showResetButton();
      this.#getSearchResults(clean);
      this.#resolveIntent(clean);
    } else {
      this.#resetSearch();
    }

    requestAnimationFrame(() => {
      input.focus({ preventScroll: true });
      input.setSelectionRange(input.value.length, input.value.length);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /* ------------------------------------------------------------------ */
  /* Dialog plumbing                                                     */
  /* ------------------------------------------------------------------ */

  /** @param {MouseEvent} event */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const interactive = target.closest('button, a, input');
    if (!interactive && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeyboardShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
    // showModal() honours autofocus, but be explicit for browsers that don't.
    requestAnimationFrame(() => {
      const input = this.refs.searchInput;
      input.focus({ preventScroll: true });
      input.setSelectionRange(input.value.length, input.value.length);
    });
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    // A prefilled term (results page hand-off, back-navigation resume) wins.
    if (this.refs.searchInput.value.trim()) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  /* ------------------------------------------------------------------ */
  /* Keyboard navigation                                                 */
  /* ------------------------------------------------------------------ */

  get #allResultsItems() {
    return /** @type {HTMLElement[]} */ (
      [
        ...Array.from(this.refs.intentSlot?.querySelectorAll('[data-ps-item]') ?? []),
        ...Array.from(this.refs.predictiveSearchResults.querySelectorAll('[data-ps-item]')),
      ].filter(
        (item) => item instanceof HTMLElement && item.offsetParent !== null
      )
    );
  }

  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    let activeItem = null;

    for (const [itemIndex, item] of items.entries()) {
      item.classList.remove('keyboard-focus');
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) item.classList.add('keyboard-focus');
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  /** @param {KeyboardEvent} event */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      // Typed something: Escape clears first. Empty: let the dialog close.
      if (this.refs.searchInput.value.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        this.#resetSearch();
        this.refs.searchInput.focus();
      }
      return;
    }

    if (event.key === 'Enter') {
      const singleResult = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
      if (singleResult instanceof HTMLElement && singleResult.dataset.singleResultUrl) {
        event.preventDefault();
        RecentSearches.add(this.refs.searchInput.value);
        SearchResume.mark(this.refs.searchInput.value);
        window.location.href = singleResult.dataset.singleResultUrl;
        return;
      }

      if (this.#currentIndex >= 0) {
        event.preventDefault();
        this.#currentItem?.querySelector('a')?.click();
        return;
      }

      // Plain Enter: submit the form (records the term in onSubmit).
      return;
    }

    const items = this.#allResultsItems;
    if (!items.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;

    const currentIndex = this.#currentIndex;
    const total = items.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < total - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : total - 1;
        break;
      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        if (event.shiftKey) this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : total - 1;
        else this.#currentIndex = currentIndex < total - 1 ? currentIndex + 1 : 0;
        break;
    }
  };

  /**
   * "Zrušit". On the results page this leaves the search altogether: back to
   * the page the search started from, skipping the results page (and any
   * sort/filter entries it added). Elsewhere it just closes the modal.
   */
  cancel = () => {
    const onResults = location.pathname === Theme.routes.search_url;
    if (!onResults) {
      this.dialog?.closeDialog();
      return;
    }

    const rec = SearchResume.record();
    SearchResume.clear();

    const steps = rec?.len ? history.length - rec.len : 0;
    if (steps > 0 && steps <= 20) {
      history.go(-steps);
      return;
    }
    if (rec?.from && !rec.from.includes(Theme.routes.search_url)) {
      location.href = rec.from;
      return;
    }
    location.href = Theme.routes.search_url.replace(/\/search$/, '') || '/';
  };

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    event.preventDefault();
    const term = this.refs.searchInput.value.trim();
    if (!term) {
      this.refs.searchInput.focus();
      return;
    }
    this.#go(term);
  };

  /**
   * Leave the modal for a typed or chosen term: the collection with filters
   * when the words map onto one (and it is not empty), else the strict
   * product search, else Shopify's loose search when even that found nothing.
   * @param {string} term
   */
  async #go(term) {
    RecentSearches.add(term);
    SearchResume.mark(term);
    if (this.#intentTerm !== term) this.#resolveIntent(term);
    const intent = await this.#intentPending;
    if (intent) {
      location.assign(intent.url);
      return;
    }
    const { q } = buildSearchQuery(term, this.#getIntentData());
    const loose = this.#looseFor === term;
    location.assign(searchPageUrl(loose ? term : q, { fallback: loose, term }));
  }

  /* ------------------------------------------------------------------ */
  /* Collection intent                                                   */
  /* ------------------------------------------------------------------ */

  /** @type {IntentData | null | undefined} */
  #intentData;
  #intentTerm = '';
  /** @type {Promise<Intent | null>} */
  #intentPending = Promise.resolve(null);
  /** @type {Map<string, Promise<{ count: number, images: string[] }>>} */
  #intentCounts = new Map();

  #getIntentData() {
    if (this.#intentData !== undefined) return this.#intentData;
    try {
      const raw = document.getElementById('search-intent-data')?.textContent;
      this.#intentData = raw ? /** @type {IntentData} */ (JSON.parse(raw)) : null;
    } catch {
      this.#intentData = null;
    }
    return this.#intentData;
  }

  /**
   * Filtered product count (and the first product images) of a collection
   * URL, via the tiny search-intent-count section. Cached per URL.
   * @param {string} collectionUrl
   */
  #intentLookup(collectionUrl) {
    const cached = this.#intentCounts.get(collectionUrl);
    if (cached) return cached;
    const url = new URL(collectionUrl, location.origin);
    url.searchParams.set('section_id', 'search-intent-count');
    const promise = fetch(url.toString())
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const text = new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
        const parsed = JSON.parse(text.trim());
        const count = Number.isFinite(parsed.count) ? parsed.count : 0;
        const images = Array.isArray(parsed.images) ? parsed.images.filter((i) => typeof i === 'string' && i) : [];
        return { count, images };
      })
      .catch(() => ({ count: 0, images: [] }));
    this.#intentCounts.set(collectionUrl, promise);
    return promise;
  }

  /** @param {string} term */
  #resolveIntent(term) {
    this.#intentTerm = term;
    const data = this.#getIntentData();
    const intent = data ? interpretIntent(term, data) : null;
    if (!intent) {
      this.#intentPending = Promise.resolve(null);
      this.#renderIntent(null);
      return;
    }
    const pending = this.#intentLookup(intent.url).then(({ count, images }) =>
      count > 0 ? { ...intent, count, images } : null
    );
    this.#intentPending = pending;
    pending.then((result) => {
      if (this.#intentTerm === term) this.#renderIntent(result);
    });
  }

  /** @param {Intent | null} intent */
  #renderIntent(intent) {
    const slot = this.refs.intentSlot;
    if (!(slot instanceof HTMLElement)) return;
    if (!intent) {
      slot.hidden = true;
      slot.replaceChildren();
      return;
    }
    const strings = this.#getIntentData()?.strings ?? {};
    const wrap = document.createElement('div');
    wrap.className = 'ps-intent';
    wrap.setAttribute('data-ps-item', '');
    wrap.setAttribute('data-ps-intent', '');

    const link = document.createElement('a');
    link.className = 'ps-intent__link';
    link.href = intent.url;

    // The first products of the filtered collection, fanned like avatars.
    const icon = document.createElement('span');
    icon.className = 'ps-intent__media';
    icon.setAttribute('aria-hidden', 'true');
    (intent.images ?? []).slice(0, 3).forEach((src) => {
      const img = document.createElement('img');
      img.className = 'ps-intent__thumb';
      img.src = src;
      img.alt = '';
      img.width = 40;
      img.height = 40;
      img.loading = 'lazy';
      icon.append(img);
    });

    const body = document.createElement('span');
    body.className = 'ps-intent__body';
    const title = document.createElement('span');
    title.className = 'ps-intent__title';
    title.textContent = intent.title;
    const meta = document.createElement('span');
    meta.className = 'ps-intent__meta';
    meta.textContent = `${strings.open ?? ''} · ${intentCountLabel(intent.count ?? 0, strings)}`;
    body.append(title, meta);

    const arrow = document.createElement('span');
    arrow.className = 'ps-intent__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 6 6 6-6 6"/></svg>';

    link.append(icon, body, arrow);
    wrap.append(link);
    slot.replaceChildren(wrap);
    slot.hidden = false;
  }

  /* ------------------------------------------------------------------ */
  /* Recent searches                                                     */
  /* ------------------------------------------------------------------ */

  /** @param {MouseEvent} event */
  #handleResultClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const remove = target.closest('[data-ps-recent-remove]');
    if (remove instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      RecentSearches.remove(remove.dataset.psRecentRemove ?? '');
      this.#paintRecentSearches();
      this.refs.searchInput.focus();
      return;
    }

    const link = target.closest('a');
    if (!(link instanceof HTMLAnchorElement)) return;

    // Leaving through any result: back should land in this search again.
    SearchResume.mark(this.refs.searchInput.value);

    if (link.closest('[data-ps-intent]')) {
      RecentSearches.add(this.refs.searchInput.value.trim());
      return;
    }

    const queryRow = link.closest('[data-ps-query]');
    if (queryRow instanceof HTMLElement && queryRow.dataset.psQuery) {
      event.preventDefault();
      this.#go(queryRow.dataset.psQuery);
      return;
    }

    const recentChip = link.closest('[data-ps-recent]');
    if (recentChip instanceof HTMLElement && recentChip.dataset.psRecent) {
      event.preventDefault();
      this.#go(recentChip.dataset.psRecent); // also bumps it to the top
      return;
    }

    // Any other result while a term is typed: the term was useful, keep it.
    const term = this.refs.searchInput.value.trim();
    if (term && link.closest('[data-search-results]')) {
      RecentSearches.add(term);
    }
  };

  clearRecentSearches = (/** @type {Event} */ event) => {
    event.preventDefault();
    RecentSearches.clear();
    this.#paintRecentSearches();
    this.refs.searchInput.focus();
  };

  #paintRecentSearches() {
    const slot = this.refs.predictiveSearchResults.querySelector('[data-ps-slot="recent-searches"]');
    const list = slot?.querySelector('[data-ps-recent-list]');
    if (!(slot instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    const terms = RecentSearches.get();
    if (!terms.length) {
      slot.hidden = true;
      list.replaceChildren();
      return;
    }

    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    const clearLabel = slot.querySelector('.ps-section__action')?.textContent?.trim() ?? '';

    list.replaceChildren(
      ...terms.map((term) => {
        const url = new URL(searchUrl);
        url.searchParams.set('q', term);
        url.searchParams.set('type', 'product');

        const li = document.createElement('li');
        li.className = 'ps-chip-item';
        li.setAttribute('data-ps-item', '');
        li.setAttribute('data-ps-recent', term);

        const a = document.createElement('a');
        a.className = 'ps-chip';
        a.href = url.toString();

        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('width', '14');
        icon.setAttribute('height', '14');
        icon.setAttribute('viewBox', '0 0 24 24');
        icon.setAttribute('fill', 'none');
        icon.setAttribute('stroke', 'currentColor');
        icon.setAttribute('stroke-width', '2.2');
        icon.setAttribute('stroke-linecap', 'round');
        icon.setAttribute('stroke-linejoin', 'round');
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>';

        const text = document.createElement('span');
        text.textContent = term;

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'ps-chip__remove';
        remove.setAttribute('data-ps-recent-remove', term);
        remove.setAttribute('aria-label', `${clearLabel}: ${term}`);
        remove.innerHTML =
          '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

        a.append(icon, text, remove);
        li.append(a);
        return li;
      })
    );

    slot.hidden = false;
  }

  /* ------------------------------------------------------------------ */
  /* Recently viewed products                                            */
  /* ------------------------------------------------------------------ */

  /** @param {Event} event */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();
    RecentlyViewed.clearProducts();

    const { recentlyViewedWrapper } = this.refs;
    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');
      recentlyViewedWrapper.addEventListener('transitionend', () => recentlyViewedWrapper.remove(), { once: true });
      setTimeout(() => recentlyViewedWrapper.remove(), 400);
    }
    this.refs.searchInput.focus();
  }

  /* ------------------------------------------------------------------ */
  /* Search                                                              */
  /* ------------------------------------------------------------------ */

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) this.refs.searchInput.focus();
    this.#resetSearch();
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
    this.#resolveIntent(searchTerm);
  }, 180);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.scrollTo(0, 0);
    });
  }

  /** The term whose strict query found nothing, so the loose one was shown. */
  #looseFor = '';

  /** @param {string} searchTerm */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();
    this.classList.add('is-loading');

    /** @param {string} q */
    const fetchMarkup = (q) => {
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[limit]', '8');
      return sectionRenderer.getSectionHTML(this.dataset.sectionId ?? '', false, url);
    };

    try {
      const { q, scoped } = buildSearchQuery(searchTerm, this.#getIntentData());
      let resultsMarkup = await fetchMarkup(q);
      if (abortController.signal.aborted) return;

      // Strict (AND) query empty, e.g. a typo in a non-last word: fall back
      // to Shopify's loose matching so the customer still sees something.
      let loose = false;
      if (scoped && resultsMarkup?.includes('data-ps-no-results')) {
        resultsMarkup = await fetchMarkup(searchTerm);
        if (abortController.signal.aborted) return;
        loose = true;
      }
      this.#looseFor = loose ? searchTerm : '';

      if (!resultsMarkup) return;
      morph(predictiveSearchResults, resultsMarkup);
      this.refs.searchInput.setAttribute('aria-expanded', 'true');
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    } finally {
      if (!abortController.signal.aborted) this.classList.remove('is-loading');
    }
  }

  /** @returns {Promise<string | null>} */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    this.#activeFetch?.abort();
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    this.#intentTerm = '';
    this.#intentPending = Promise.resolve(null);
    this.#renderIntent(null);
    searchInput.value = '';
    searchInput.setAttribute('aria-expanded', 'false');
    this.#hideResetButton();
    this.classList.remove('is-loading');

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    if (RecentlyViewed.getProducts().length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (recentlyViewedMarkup) {
        const parsed = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const block = parsed.getElementById('predictive-search-products');
        const slot = parsedEmptySectionMarkup.querySelector('[data-ps-slot="recent-products"]');
        if (block && slot) {
          block.id = 'predictive-search-recent';
          slot.replaceChildren(block);
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#paintRecentSearches();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
